import { useState } from "react";
import { Box, LinearProgress, Stack, useMediaQuery } from "@mui/material";
// MUI's useTheme (not emotion's) so the component still resolves breakpoints
// when it is rendered outside a ThemeProvider.
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import {
  MobileEmpty,
  MobileListItem,
  MobileListShell,
  MobilePagination,
  MobileSkeletonList,
  MobileSummaryCard,
} from "./index";

/**
 * A DataGrid that turns into the mobile card list below `md`.
 *
 * The card content is derived from the SAME `columns` array the grid uses, so a
 * table only has to opt in — there is no second copy of the row markup to keep
 * in sync. Each column becomes a labelled stat tile (`headerName` → label,
 * `renderCell`/`valueGetter` → value).
 *
 * The `mobile` prop tunes the card without duplicating anything:
 *   titleField      field used as the card heading (default: first text column)
 *   title           (row, index) => node — overrides titleField
 *   subtitleField   field shown under the heading
 *   subtitle        (row, index) => node
 *   highlightField  field promoted to the big coloured amount block
 *   highlightLabel  label for that block (default: the column's headerName)
 *   highlightUnit   e.g. "IQD"
 *   highlightTone   "auto" colours by sign, or a hex tone
 *   actionFields    fields rendered as the card's action row
 *                   (default: any column whose field looks like an action)
 *   hiddenFields    fields to leave out of the card
 *   fullWidthFields fields that should span both tile columns
 *   avatar          node shown in the rounded icon tile
 *   avatarTone      hex colour for that tile
 *   badgeField      field rendered as the pill next to the heading
 *   noteField       field rendered in the tinted note box
 *   getKey          (row, index) => key
 */

const ACTION_FIELD_PATTERN = /^(operations|actions|action|options)$/i;

const isActionColumn = (col, actionFields) => {
  if (actionFields) return actionFields.includes(col.field);
  if (col.type === "actions") return true;
  if (ACTION_FIELD_PATTERN.test(col.field)) return true;
  // The Arabic tables label this column in several ways.
  return typeof col.headerName === "string" && col.headerName.includes("العمليات");
};

// Build a params object shaped like the one the grid passes to renderCell.
// `api` is stubbed with the two row-index helpers a few tables rely on.
const buildParams = (row, col, index, getRowId) => {
  const id = getRowId ? getRowId(row) : row.id;
  const rawValue = row?.[col.field];
  const params = {
    row,
    id,
    field: col.field,
    colDef: col,
    value: rawValue,
    api: {
      getRowIndex: () => index,
      getRowIndexRelativeToVisibleRows: () => index,
    },
  };
  if (typeof col.valueGetter === "function") {
    try {
      params.value = col.valueGetter(params);
    } catch (e) {
      params.value = rawValue;
    }
  }
  return params;
};

const renderColumnValue = (row, col, index, getRowId) => {
  const params = buildParams(row, col, index, getRowId);
  if (typeof col.renderCell === "function") {
    try {
      return col.renderCell(params);
    } catch (e) {
      // A renderCell that needs the real grid api falls back to the raw value
      // rather than taking the whole page down.
      return params.value ?? null;
    }
  }
  return params.value ?? null;
};

const findColumn = (columns, field) =>
  field ? columns.find((c) => c.field === field) : undefined;

// Row-number columns carry no meaning on a card, which already shows its
// position in the avatar tile.
const INDEX_FIELD_PATTERN = /^(index|sequence|no|number_index)$/i;
const isIndexColumn = (col) =>
  INDEX_FIELD_PATTERN.test(col.field) || col.headerName === "#";

const ResponsiveDataGrid = ({
  rows = [],
  columns = [],
  mobile = {},
  /** Height of the desktop grid wrapper. Pass null to skip the wrapper. */
  gridHeight = "70vh",
  getRowId,
  loading,
  // Pagination props are shared: the grid and the mobile pager read the same
  // state, so paging behaves identically on both.
  page,
  pageSize,
  rowCount,
  onPageChange,
  onPageSizeChange,
  rowsPerPageOptions = [15, 25, 50, 100],
  components,
  ...gridProps
}) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Several grids drive paging entirely from their onPageChange handler and
  // never pass `page`/`pageSize` back in. The mobile pager still needs a
  // current position to render, so mirror it locally when it isn't supplied.
  const [fallbackPage, setFallbackPage] = useState(0);
  const [fallbackPageSize, setFallbackPageSize] = useState(
    pageSize ?? rowsPerPageOptions[0]
  );
  const currentPage = page ?? fallbackPage;
  const currentPageSize = pageSize ?? fallbackPageSize;

  const handlePageChange = (newPage) => {
    setFallbackPage(newPage);
    onPageChange?.(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setFallbackPageSize(newPageSize);
    setFallbackPage(0);
    onPageSizeChange?.(newPageSize);
  };

  const dataGrid = (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      loading={loading}
      page={page}
      pageSize={pageSize}
      rowCount={rowCount}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      rowsPerPageOptions={rowsPerPageOptions}
      {...gridProps}
      components={{ LoadingOverlay: LinearProgress, ...components }}
    />
  );

  if (!smallScreen) {
    return gridHeight ? (
      <Box sx={{ height: gridHeight, width: "100%" }}>{dataGrid}</Box>
    ) : (
      dataGrid
    );
  }

  const {
    titleField,
    title,
    subtitleField,
    subtitle,
    highlightField,
    highlightLabel,
    highlightUnit,
    highlightTone,
    actionFields,
    hiddenFields = [],
    fullWidthFields = [],
    avatar,
    avatarTone,
    badgeField,
    noteField,
    getKey,
    skeletonCount = 4,
    emptyMessage,
    summary,
  } = mobile;

  const actionColumns = columns.filter((c) => isActionColumn(c, actionFields));
  const actionFieldNames = actionColumns.map((c) => c.field);

  // With no explicit titleField, promote the first meaningful column so the
  // card still reads as "<name>" rather than as an unlabelled tile grid.
  const autoTitleColumn =
    titleField || title
      ? undefined
      : columns.find(
          (c) =>
            !isIndexColumn(c) &&
            !isActionColumn(c, actionFields) &&
            c.headerName &&
            !hiddenFields.includes(c.field)
        );

  // Everything the header/highlight/badge/note already shows is dropped from
  // the tile grid so nothing is printed twice.
  const consumed = new Set(
    [
      titleField,
      autoTitleColumn?.field,
      subtitleField,
      highlightField,
      badgeField,
      noteField,
      ...hiddenFields,
      ...actionFieldNames,
    ].filter(Boolean)
  );

  const tileColumns = columns.filter(
    (c) => !consumed.has(c.field) && c.headerName && !isIndexColumn(c)
  );

  const highlightColumn = findColumn(columns, highlightField);
  const titleColumn = findColumn(columns, titleField) ?? autoTitleColumn;
  const subtitleColumn = findColumn(columns, subtitleField);
  const badgeColumn = findColumn(columns, badgeField);
  const noteColumn = findColumn(columns, noteField);

  // Only worth showing when the grid actually pages server-side.
  const showPager =
    typeof rowCount === "number" &&
    rowCount > 0 &&
    typeof onPageChange === "function";

  return (
    <MobileListShell loading={loading && rows.length > 0}>
      {summary ? (
        <MobileSummaryCard
          label={summary.label}
          value={summary.value}
          unit={summary.unit}
          icon={summary.icon}
        />
      ) : null}
      {loading && rows.length === 0 ? (
        <MobileSkeletonList count={skeletonCount} />
      ) : rows.length === 0 ? (
        <MobileEmpty message={emptyMessage} />
      ) : (
        <>
          {rows.map((row, index) => (
            <MobileListItem
              key={
                getKey
                  ? getKey(row, index)
                  : getRowId
                  ? getRowId(row)
                  : row.id ?? index
              }
              avatar={avatar ?? index + 1}
              avatarTone={avatarTone}
              title={
                title
                  ? title(row, index)
                  : titleColumn
                  ? renderColumnValue(row, titleColumn, index, getRowId)
                  : undefined
              }
              subtitle={
                subtitle
                  ? subtitle(row, index)
                  : subtitleColumn
                  ? renderColumnValue(row, subtitleColumn, index, getRowId)
                  : undefined
              }
              badge={
                badgeColumn
                  ? renderColumnValue(row, badgeColumn, index, getRowId)
                  : undefined
              }
              highlight={
                highlightColumn
                  ? {
                      label: highlightLabel ?? highlightColumn.headerName,
                      value: renderColumnValue(
                        row,
                        highlightColumn,
                        index,
                        getRowId
                      ),
                      rawValue: row?.[highlightColumn.field],
                      unit: highlightUnit,
                      tone: highlightTone,
                    }
                  : undefined
              }
              fields={tileColumns.map((col) => ({
                label: col.headerName,
                value: renderColumnValue(row, col, index, getRowId),
                span: fullWidthFields.includes(col.field) ? 12 : 6,
              }))}
              note={
                noteColumn
                  ? renderColumnValue(row, noteColumn, index, getRowId)
                  : undefined
              }
              actions={
                actionColumns.length ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    justifyContent="center"
                  >
                    {actionColumns.map((col) => (
                      <Box key={col.field} sx={{ display: "flex", gap: 0.5 }}>
                        {col.type === "actions" &&
                        typeof col.getActions === "function"
                          ? col.getActions(
                              buildParams(row, col, index, getRowId)
                            )
                          : renderColumnValue(row, col, index, getRowId)}
                      </Box>
                    ))}
                  </Stack>
                ) : undefined
              }
            />
          ))}
          {showPager && (
            <MobilePagination
              count={rowCount ?? 0}
              rowsPerPage={currentPageSize}
              page={currentPage}
              onPageChange={(e, newPage) => handlePageChange(newPage)}
              rowsPerPageOptions={rowsPerPageOptions}
              onRowsPerPageChange={(event) =>
                handlePageSizeChange(parseInt(event.target.value, 10))
              }
            />
          )}
        </>
      )}
    </MobileListShell>
  );
};

export default ResponsiveDataGrid;
